import React, { useState, useEffect } from 'react';
import { FaQuestion, FaSearch, FaPlus, FaFile, FaVideo, FaLink, FaComments, FaPaperPlane, FaArrowRight } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Component for students to access support resources and submit support tickets
 */
const HelpCenter = () => {
  const { showSuccess, showError } = useToast();
  
  // State for FAQs
  const [faqs, setFaqs] = useState([]);
  const [expandedFaqId, setExpandedFaqId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredFaqs, setFilteredFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // State for support ticket form
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    message: '',
    category: 'general'
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // State for resources
  const [resources, setResources] = useState([]);
  
  // Category options for support tickets
  const categoryOptions = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'technical', label: 'Technical Issue' },
    { value: 'billing', label: 'Billing Query' },
    { value: 'feedback', label: 'Feedback' },
    { value: 'mentor', label: 'Mentor Related' }
  ];
  
  // Fetch FAQs and resources on component mount
  useEffect(() => {
    Promise.all([
      fetchFaqs(),
      fetchResources()
    ]);
  }, []);
  
  // Filter FAQs when search term changes
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredFaqs(faqs);
    } else {
      const searchTermLower = searchTerm.toLowerCase();
      const filtered = faqs.filter(faq => 
        faq.question.toLowerCase().includes(searchTermLower) || 
        faq.answer.toLowerCase().includes(searchTermLower)
      );
      setFilteredFaqs(filtered);
    }
  }, [searchTerm, faqs]);
  
  // Fetch FAQs from API
  const fetchFaqs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/support/faqs');
      
      // Sort FAQs by category
      const sortedFaqs = response.data.data.sort((a, b) => 
        a.category.localeCompare(b.category)
      );
      
      setFaqs(sortedFaqs);
      setFilteredFaqs(sortedFaqs);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      showError('Failed to load FAQ data');
      setLoading(false);
    }
  };
  
  // Fetch resources from API
  const fetchResources = async () => {
    try {
      const response = await api.get('/support/resources');
      setResources(response.data.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
      // Don't show error toast for resources as it's secondary content
    }
  };
  
  // Handle support ticket form field changes
  const handleTicketChange = (e) => {
    const { name, value } = e.target;
    
    setTicketForm({
      ...ticketForm,
      [name]: value
    });
    
    // Clear error for this field
    setFormErrors({
      ...formErrors,
      [name]: undefined
    });
  };
  
  // Toggle FAQ expansion
  const toggleFaq = (faqId) => {
    if (expandedFaqId === faqId) {
      setExpandedFaqId(null);
    } else {
      setExpandedFaqId(faqId);
    }
  };
  
  // Validate support ticket form
  const validateTicketForm = () => {
    const errors = {};
    
    if (!ticketForm.subject.trim()) errors.subject = 'Please enter a subject';
    if (!ticketForm.message.trim()) errors.message = 'Please enter a message';
    if (!ticketForm.category) errors.category = 'Please select a category';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle support ticket form submission
  const handleTicketSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateTicketForm()) return;
    
    try {
      setSubmitting(true);
      
      await api.post('/support/tickets', ticketForm);
      
      showSuccess('Support ticket submitted successfully');
      
      // Reset form
      setTicketForm({
        subject: '',
        message: '',
        category: 'general'
      });
    } catch (error) {
      console.error('Error submitting support ticket:', error);
      showError(error.response?.data?.message || 'Failed to submit support ticket');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Get resource icon based on type
  const getResourceIcon = (type) => {
    switch (type) {
      case 'document':
        return <FaFile className="text-blue-500" />;
      case 'video':
        return <FaVideo className="text-red-500" />;
      case 'link':
        return <FaLink className="text-green-500" />;
      default:
        return <FaFile className="text-gray-500" />;
    }
  };
  
  // Group FAQs by category
  const getFaqsByCategory = () => {
    const groupedFaqs = {};
    
    filteredFaqs.forEach(faq => {
      if (!groupedFaqs[faq.category]) {
        groupedFaqs[faq.category] = [];
      }
      groupedFaqs[faq.category].push(faq);
    });
    
    return groupedFaqs;
  };
  
  // Create a nice display name for category
  const formatCategoryName = (category) => {
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <h1 className="text-2xl font-bold flex items-center">
          <FaQuestion className="mr-2 text-primary" /> Help Center
        </h1>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* FAQs Section - 2/3 width on large screens */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold mb-4">Frequently Asked Questions</h2>
            
            {/* Search */}
            <div className="relative mb-6">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                className="form-input pl-10 w-full"
                placeholder="Search FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            {loading ? (
              <div className="flex justify-center py-6">
                <LoadingSpinner size="lg" />
              </div>
            ) : filteredFaqs.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No FAQs found matching your search.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(getFaqsByCategory()).map(([category, categoryFaqs]) => (
                  <div key={category}>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      {formatCategoryName(category)}
                    </h3>
                    <div className="space-y-4">
                      {categoryFaqs.map(faq => (
                        <div key={faq._id} className="border border-gray-200 rounded-lg overflow-hidden">
                          <button
                            className="w-full flex justify-between items-center p-4 text-left focus:outline-none hover:bg-gray-50"
                            onClick={() => toggleFaq(faq._id)}
                          >
                            <span className="font-medium text-gray-900">{faq.question}</span>
                            <span className={`transform transition-transform ${expandedFaqId === faq._id ? 'rotate-180' : ''}`}>
                              <FaArrowRight className="text-gray-400" />
                            </span>
                          </button>
                          {expandedFaqId === faq._id && (
                            <div className="px-4 pb-4 text-gray-600">
                              <div className="pt-2 border-t border-gray-200">
                                {faq.answer}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Support Ticket and Resources Section - 1/3 width on large screens */}
        <div className="lg:col-span-1">
          {/* Submit Support Ticket */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaComments className="mr-2 text-primary" /> Contact Support
            </h2>
            
            <form onSubmit={handleTicketSubmit}>
              <div className="space-y-4">
                {/* Category */}
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={ticketForm.category}
                    onChange={handleTicketChange}
                    className={`form-select w-full ${formErrors.category ? 'border-red-500' : ''}`}
                    disabled={submitting}
                  >
                    {categoryOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {formErrors.category && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.category}</p>
                  )}
                </div>
                
                {/* Subject */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={ticketForm.subject}
                    onChange={handleTicketChange}
                    className={`form-input w-full ${formErrors.subject ? 'border-red-500' : ''}`}
                    disabled={submitting}
                    placeholder="Brief description of your issue"
                  />
                  {formErrors.subject && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.subject}</p>
                  )}
                </div>
                
                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={ticketForm.message}
                    onChange={handleTicketChange}
                    rows="5"
                    className={`form-textarea w-full ${formErrors.message ? 'border-red-500' : ''}`}
                    disabled={submitting}
                    placeholder="Please provide details about your question or issue"
                  ></textarea>
                  {formErrors.message && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.message}</p>
                  )}
                </div>
                
                {/* Submit Button */}
                <div>
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <LoadingSpinner size="sm" className="mr-2" />
                    ) : (
                      <FaPaperPlane className="mr-2" />
                    )}
                    Submit Ticket
                  </button>
                </div>
              </div>
            </form>
          </div>
          
          {/* Resources */}
          <div className="bg-white rounded-lg shadow-soft p-6 mt-6">
            <h2 className="text-xl font-semibold mb-4">Helpful Resources</h2>
            
            {resources.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No resources available.</p>
            ) : (
              <ul className="divide-y divide-gray-200">
                {resources.map(resource => (
                  <li key={resource._id} className="py-3">
                    <a 
                      href={resource.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center hover:text-primary"
                    >
                      <div className="mr-3">
                        {getResourceIcon(resource.type)}
                      </div>
                      <div>
                        <h3 className="text-sm font-medium">{resource.title}</h3>
                        <p className="text-xs text-gray-500 mt-1">{resource.description}</p>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            )}
            
            {/* Add Link to Full Documentation */}
            <div className="mt-4 text-center">
              <a 
                href="/documentation" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center text-primary hover:text-primary-dark font-medium"
              >
                View Full Documentation
                <FaArrowRight className="ml-2" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter; 